import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Avatar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import {
  Shield as ShieldIcon,
  AdminPanelSettings as AdminIcon,
  Groups as GroupIcon,
  Person as PersonIcon,
  SupervisorAccount as SupervisorIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { PageContainer, RoleGuard } from '../../../components';
import { 
  useNotification, 
  usePermissionsContext,
  useCompanies
} from '../../../hooks';
import { roleManagementStyles } from './RoleManagementPage.styles';
import { 
  SCREENS, 
  ACTIONS, 
  USER_ROLES, 
  ROLE_CONFIG as CENTRALIZED_ROLE_CONFIG,
  PERMISSION_CONFIG as CENTRALIZED_PERMISSION_CONFIG,
  SCREEN_CONFIG as CENTRALIZED_SCREEN_CONFIG,
  getScreensArray
} from '../../../constants/permissions';
import { hasuraClient } from '../../../utils/graphqlClient';
import { GET_ALL_PERMISSIONS_QUERY } from '../../../graphql';


const RoleManagementPage = () => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const { user } = usePermissionsContext();
  const { companies, isLoading: companiesLoading } = useCompanies();
  
  const [allPermissions, setAllPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);

  const canModifyRole = useCallback(role => role !== 'ROOT', []);

  const fetchAllPermissions = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await hasuraClient.query(GET_ALL_PERMISSIONS_QUERY);
      setAllPermissions(data.permissions);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      showNotification('hooks.permissions.errors.fetchList', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [showNotification]);

  const createPermission = useCallback(async (permissionData) => {
    try {
      setIsUpdating(true);
      const CREATE_PERMISSION_MUTATION = `
        mutation CreatePermission($permission: permissions_insert_input!) {
          insert_permissions_one(object: $permission) {
            id
            role
            screen
            company_id
            can_create
            can_view
            can_edit
            can_delete
            can_download
          }
        }
      `;
      
      const result = await hasuraClient.mutate(CREATE_PERMISSION_MUTATION, {
        permission: permissionData
      });
      
      return {
        success: true,
        data: result.insert_permissions_one
      };
    } catch (error) {
      console.error('Error creating permission:', error);
      showNotification('hooks.permissions.errors.create', 'error');
      return { success: false, error };
    } finally {
      setIsUpdating(false);
    }
  }, [showNotification]);

  const updatePermission = useCallback(async (permissionId, updateData) => {
    try {
      setIsUpdating(true);
      const UPDATE_PERMISSION_MUTATION = `
        mutation UpdatePermission($id: Int!, $updates: permissions_set_input!) {
          update_permissions_by_pk(pk_columns: {id: $id}, _set: $updates) {
            id
            role
            screen
            company_id
            can_create
            can_view
            can_edit
            can_delete
            can_download
          }
        }
      `;
      
      const result = await hasuraClient.mutate(UPDATE_PERMISSION_MUTATION, {
        id: permissionId,
        updates: updateData
      });
      
      return {
        success: true,
        data: result.update_permissions_by_pk
      };
    } catch (error) {
      console.error('Error updating permission:', error);
      showNotification('hooks.permissions.errors.update', 'error');
      return { success: false, error };
    } finally {
      setIsUpdating(false);
    }
  }, [showNotification]);

  useEffect(() => {
    if (companies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [companies, selectedCompanyId]);

  useEffect(() => {
    fetchAllPermissions();
  }, [fetchAllPermissions]);

  const getPermissionData = useCallback((role, screen) => {
    return allPermissions.find(
      p => p.role === role && p.screen === screen && p.company_id === selectedCompanyId
    );
  }, [allPermissions, selectedCompanyId]);

  const handlePermissionChange = useCallback(
    async (role, screen, permissionField, value) => {
      if (!canModifyRole(role)) {
        showNotification('settings.messages.rootProtected', 'error');
        return;
      }

      const permissionData = getPermissionData(role, screen);

      try {
        if (!permissionData) {
          const newPermissionData = {
            role,
            screen,
            company_id: selectedCompanyId,
            can_create: permissionField === 'can_create' ? value : false,
            can_view: permissionField === 'can_view' ? value : false,
            can_edit: permissionField === 'can_edit' ? value : false,
            can_delete: permissionField === 'can_delete' ? value : false,
            can_download: permissionField === 'can_download' ? value : false,
          };

          const result = await createPermission(newPermissionData);

          if (result.success) {
            setAllPermissions(prev => [...prev, result.data]);
          }
        } else {
          const result = await updatePermission(permissionData.id, {
            [permissionField]: value,
          });

          if (result.success) {
            setAllPermissions(prev =>
              prev.map(p =>
                p.id === permissionData.id
                  ? { ...p, [permissionField]: value }
                  : p
              )
            );
          }
        }
      } catch (error) {
        console.error('Error updating/creating permission:', error);
      }
    },
    [canModifyRole, showNotification, getPermissionData, updatePermission, createPermission, selectedCompanyId]
  );

    const selectedCompany = useMemo(() => {
    return companies.find(c => c.id === selectedCompanyId);
  }, [companies, selectedCompanyId]);

  const UI_ROLE_CONFIG = useMemo(() => ({
    [USER_ROLES.ROOT]: {
      ...CENTRALIZED_ROLE_CONFIG[USER_ROLES.ROOT],
      label: t('settings.roles.ROOT'),
      description: t('settings.roleDescriptions.ROOT'),
      icon: <ShieldIcon />,
    },
    [USER_ROLES.DIRECTOR]: {
      ...CENTRALIZED_ROLE_CONFIG[USER_ROLES.DIRECTOR],
      label: t('settings.roles.DIRECTOR'),
      description: t('settings.roleDescriptions.DIRECTOR'),
      icon: <AdminIcon />,
    },
    [USER_ROLES.GROUP_MANAGER]: {
      ...CENTRALIZED_ROLE_CONFIG[USER_ROLES.GROUP_MANAGER],
      label: t('settings.roles.GROUP_MANAGER'),
      description: t('settings.roleDescriptions.GROUP_MANAGER'),
      icon: <GroupIcon />,
    },
    [USER_ROLES.TEAM_MANAGER]: {
      ...CENTRALIZED_ROLE_CONFIG[USER_ROLES.TEAM_MANAGER],
      label: t('settings.roles.TEAM_MANAGER'),
      description: t('settings.roleDescriptions.TEAM_MANAGER'),
      icon: <SupervisorIcon />,
    },
    [USER_ROLES.TEAM_MEMBER]: {
      ...CENTRALIZED_ROLE_CONFIG[USER_ROLES.TEAM_MEMBER],
      label: t('settings.roles.TEAM_MEMBER'),
      description: t('settings.roleDescriptions.TEAM_MEMBER'),
      icon: <PersonIcon />,
    },
  }), [t]);

  const SCREEN_CONFIG = useMemo(() => getScreensArray().map(screen => ({
    key: screen,
    name: t(`settings.pages.${screen.toLowerCase()}`) || CENTRALIZED_SCREEN_CONFIG[screen]?.name || screen
  })), [t]);

  const PERMISSION_CONFIG = useMemo(() => Object.entries(ACTIONS).map(([key, value]) => ({
    key: value,
    name: t(`settings.permissions.${value.toLowerCase()}`) || CENTRALIZED_PERMISSION_CONFIG[value]?.name || key,
    color: CENTRALIZED_PERMISSION_CONFIG[value]?.color || 'default'
  })), [t]);

  const permissionSummary = useMemo(() => {
    return PERMISSION_CONFIG.map(permission => ({
      ...permission,
      count: allPermissions.filter(p => 
        p.company_id === selectedCompanyId && p[permission.key] === true
      ).length,
    }));
  }, [allPermissions, selectedCompanyId, PERMISSION_CONFIG]);

  if (isLoading || companiesLoading || !selectedCompanyId) {
    return (
      <PageContainer currentPage={t('settings.titles.roleManagement')}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 300,
            py: 6,
            width: '100%',
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary', fontWeight: 500 }}>
            {t('settings.roleManagement.loading', 'Rol yönetimi yükleniyor...')}
          </Typography>
        </Box>
      </PageContainer>
    );
  }

  return (
    <RoleGuard roles={['ROOT']}>
      <PageContainer currentPage={t('settings.titles.roleManagement')}>
        <Box sx={roleManagementStyles.container}>
          <Box sx={roleManagementStyles.headerBox}>
            <Typography variant="h5" sx={roleManagementStyles.headerTitle}>
              {t('settings.titles.roleManagement')}
            </Typography>
            <Typography variant="body1" sx={roleManagementStyles.headerSubtitle}>
              {t('settings.roleManagement.subtitle')}
            </Typography>
          </Box>

          {/* Company Selector */}
          <Paper
            elevation={3}
            sx={roleManagementStyles.companySelectorPaper}
          >
            <Box sx={roleManagementStyles.companySelectorHeader}>
              <Avatar sx={roleManagementStyles.companySelectorAvatar}>
                <BusinessIcon />
              </Avatar>
              <Box sx={roleManagementStyles.companySelectorHeaderContent}>
                <Typography variant="h6" sx={roleManagementStyles.companySelectorTitle}>
                  {t('settings.roleManagement.companySelector.title')}
                </Typography>
                <Typography variant="body2" sx={roleManagementStyles.companySelectorSubtitle}>
                  {t('settings.roleManagement.companySelector.subtitle')}
                </Typography>
              </Box>
            </Box>
            
            <FormControl variant="outlined" sx={roleManagementStyles.companySelectorFormControl}>
              <InputLabel sx={roleManagementStyles.companySelectorInputLabel}>
                {t('settings.roleManagement.companySelector.selectCompany')}
              </InputLabel>
              <Select
                value={selectedCompanyId || ''}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                label={t('settings.roleManagement.companySelector.selectCompany')}
                sx={roleManagementStyles.companySelectorSelect}
              >
                {companies.map(company => (
                  <MenuItem key={company.id} value={company.id}>
                    <Box sx={roleManagementStyles.companySelectorMenuItem}>
                      <BusinessIcon fontSize="small" />
                      <Typography>{company.name}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedCompany && (
              <Box sx={roleManagementStyles.selectedCompanyBox}>
                <Chip
                  label={`${t('settings.roleManagement.companySelector.selected')}: ${selectedCompany.name}`}
                  size="small"
                  sx={roleManagementStyles.selectedCompanyChip}
                />
              </Box>
            )}
          </Paper>

          <Alert
            severity="warning"
            sx={roleManagementStyles.warningAlert}
            icon={<ShieldIcon />}
          >
            <Typography variant="body2">
              {t('settings.messages.rootProtected')}
            </Typography>
          </Alert>

          <Grid container spacing={3} sx={roleManagementStyles.rolesGrid}>
            {Object.entries(UI_ROLE_CONFIG).map(([roleKey, roleConfig]) => {
              const canModify = canModifyRole(roleKey);
              const isProtected = roleConfig.protected;

              return (
                <Grid item xs={12} key={roleKey}>
                  <Card sx={roleManagementStyles.roleCard(canModify, isProtected)}>
                    {!canModify && (
                      <Chip
                        label={t('settings.roleManagement.protected')}
                        size="small"
                        color="warning"
                        clickable={false}
                        sx={roleManagementStyles.protectedChip}
                      />
                    )}

                    <CardHeader
                      avatar={
                        <Avatar sx={roleManagementStyles.roleAvatar(roleConfig.color)}>
                          {roleConfig.icon}
                        </Avatar>
                      }
                      title={
                        <Box sx={roleManagementStyles.roleTitleBox}>
                          <Typography variant="h6" sx={roleManagementStyles.roleTitle}>
                            {roleConfig.label}
                          </Typography>
                          <Chip
                            label={roleKey}
                            size="small"
                            color={roleConfig.color}
                            variant="outlined"
                            clickable={false}
                            sx={roleManagementStyles.roleKeyChip}
                          />
                          {isProtected && (
                            <Chip
                              label={t('settings.roleManagement.protected')}
                              size="small"
                              color="error"
                              variant="filled"
                              clickable={false}
                              sx={roleManagementStyles.roleProtectedChip}
                            />
                          )}
                        </Box>
                      }
                      subheader={
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={roleManagementStyles.roleDescription}
                        >
                          {roleConfig.description}
                        </Typography>
                      }
                    />

                    <CardContent sx={roleManagementStyles.roleContent}>
                      <Grid container spacing={2}>
                        {SCREEN_CONFIG.map(screen => {
                          const permissionData = getPermissionData(roleKey, screen.key);
                          
                          return (
                            <Grid item xs={12} sm={6} md={4} key={screen.key}>
                              <Box sx={roleManagementStyles.screenBox}>
                                <Typography
                                  variant="subtitle2"
                                  sx={roleManagementStyles.screenTitle}
                                >
                                  {screen.name}
                                </Typography>

                                <Box sx={roleManagementStyles.permissionsBox}>
                                  {PERMISSION_CONFIG.map(permission => {
                                    const hasPermission = permissionData?.[permission.key] || false;

                                    return (
                                      <FormControlLabel
                                        key={permission.key}
                                        control={
                                          <Switch
                                            checked={hasPermission}
                                            onChange={e =>
                                              handlePermissionChange(
                                                roleKey,
                                                screen.key,
                                                permission.key,
                                                e.target.checked
                                              )
                                            }
                                            disabled={!canModify || isUpdating}
                                            size="small"
                                            color={permission.color}
                                            sx={roleManagementStyles.permissionSwitch(hasPermission)}
                                          />
                                        }
                                        label={
                                          <Typography
                                            variant="caption"
                                            sx={roleManagementStyles.permissionLabel(hasPermission)}
                                          >
                                            {permission.name}
                                          </Typography>
                                        }
                                        sx={roleManagementStyles.permissionFormControlLabel(canModify)}
                                      />
                                    );
                                  })}
                                </Box>
                              </Box>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Card sx={roleManagementStyles.summaryCard}>
            <CardHeader
              title={
                <Typography variant="h6" sx={roleManagementStyles.summaryTitle}>
                  📊 {t('settings.roleManagement.permissionSummary')}
                </Typography>
              }
              sx={roleManagementStyles.summaryHeader}
            />
            <CardContent>
              <Grid container spacing={3}>
                {permissionSummary.map(permission => (
                  <Grid item xs={12} sm={6} md={3} key={permission.key}>
                    <Box sx={roleManagementStyles.summaryStatBox(permission.color)}>
                      <Typography variant="h3" sx={roleManagementStyles.summaryStatNumber}>
                        {permission.count}
                      </Typography>
                      <Typography variant="body2" sx={roleManagementStyles.summaryStatLabel}>
                        {permission.name} {t('settings.roleManagement.permission')}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Box>
      </PageContainer>
    </RoleGuard>
  );
};

export default RoleManagementPage;
